import { useId, useRef, useState } from "react";
import { Popover } from "radix-ui";
import { motion } from "motion/react";
import { X } from "@phosphor-icons/react";
import { findPlace, searchPlaces, type Place } from "../../shared/places.ts";
import { cn } from "./cn";
import { useField } from "./Field";
import { spring } from "./motion";

const KIND: Record<Place["kind"], string> = { country: "Country", state: "State", city: "City" };
const SHOWN = 8;

export type PlacePickerProps = {
  value: string[];
  onChange: (v: string[]) => void;
  placeholder?: string;
};

/**
 * Several places from the shared list: type to filter, pick with the arrow keys and Enter or a click, remove with a
 * chip or Backspace. A value saved before the list shows as the place it spells, or as typed when it spells none.
 */
export function PlacePicker({ value, onChange, placeholder = "Search a city, state or country" }: PlacePickerProps) {
  const field = useField();
  const id = useId();
  const input = useRef<HTMLInputElement>(null);
  const anchor = useRef<HTMLDivElement>(null);
  const [text, setText] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);

  const picked = new Set(value.map((v) => findPlace(v)?.label ?? v));
  const options = text.trim() ? searchPlaces(text, SHOWN + picked.size).filter((p) => !picked.has(p.label)).slice(0, SHOWN) : [];
  const shown = open && !!text.trim();
  const at = Math.min(active, options.length - 1);
  const optionId = (i: number) => `${id}-option-${i}`;

  const add = (p: Place) => {
    onChange([...value, p.label]);
    setText("");
    setActive(0);
  };
  const remove = (v: string) => {
    onChange(value.filter((x) => x !== v));
    input.current?.focus();
  };

  return (
    <Popover.Root open={shown} onOpenChange={setOpen}>
      <Popover.Anchor asChild>
        <div
          ref={anchor}
          onClick={(e) => e.target === e.currentTarget && input.current?.focus()}
          className="flex min-h-10 flex-wrap items-center gap-1.5 rounded-control border border-line-strong bg-surface px-1.5 py-1 transition-[border-color,box-shadow] duration-150 hover:border-ink/25 focus-within:border-primary focus-within:ring-3 focus-within:ring-primary/15"
        >
          {value.map((v) => {
            const label = findPlace(v)?.label;
            return (
              <button
                key={v}
                type="button"
                onClick={() => remove(v)}
                aria-label={`Remove ${label ?? v}`}
                title={label ? undefined : "Not a place on the list. It matches postings that name it."}
                className={cn(
                  "inline-flex h-7 items-center gap-1 rounded-chip pl-2.5 pr-1.5 text-[13px] font-semibold transition-[background-color] duration-150",
                  label ? "bg-tone-brand text-tone-brand-ink hover:bg-primary-tint" : "bg-surface text-ink-soft ring-1 ring-inset ring-line-strong hover:bg-sunk",
                )}
              >
                {label ?? v}
                <X size={14} weight="bold" aria-hidden />
              </button>
            );
          })}
          <input
            ref={input}
            id={field?.id}
            aria-describedby={field?.describedBy}
            role="combobox"
            aria-expanded={shown}
            aria-controls={shown && options.length ? `${id}-list` : undefined}
            aria-autocomplete="list"
            aria-activedescendant={shown && at >= 0 ? optionId(at) : undefined}
            autoComplete="off"
            spellCheck={false}
            value={text}
            placeholder={value.length ? "Add another" : placeholder}
            onChange={(e) => {
              setText(e.target.value);
              setActive(0);
              setOpen(true);
            }}
            onFocus={() => {
              setOpen(true);
              anchor.current?.scrollIntoView({ block: "center", behavior: "smooth" });
            }}
            onBlur={() => setOpen(false)}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown" || e.key === "ArrowUp") {
                if (!options.length) return;
                e.preventDefault();
                const step = e.key === "ArrowDown" ? 1 : -1;
                setActive(shown ? (at + step + options.length) % options.length : 0);
                setOpen(true);
              } else if (e.key === "Enter") {
                if (text.trim()) e.preventDefault();
                if (shown && options[at]) add(options[at]);
              } else if (e.key === "Escape" && shown) {
                e.preventDefault();
                setOpen(false);
              } else if (e.key === "Backspace" && !text && value.length) {
                onChange(value.slice(0, -1));
              }
            }}
            className="h-8 min-w-40 flex-1 bg-transparent px-1.5 text-[15px] text-ink placeholder:text-muted/80 focus:outline-none"
          />
        </div>
      </Popover.Anchor>
      <span className="sr-only" aria-live="polite">
        {shown ? (options.length ? `${options.length} ${options.length === 1 ? "place" : "places"}` : "No places match") : ""}
      </span>
      <Popover.Portal>
        <Popover.Content
          role="presentation"
          side="bottom"
          avoidCollisions={false}
          align="start"
          sideOffset={6}
          collisionPadding={12}
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
          onInteractOutside={(e) => anchor.current?.contains(e.target as Node) && e.preventDefault()}
          className="z-50 w-[var(--radix-popover-trigger-width)] outline-none"
        >
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={spring}
            className="max-h-[min(20rem,var(--radix-popover-content-available-height))] overflow-y-auto rounded-card bg-surface p-1 shadow-raised"
          >
            {options.length ? (
              <ul id={`${id}-list`} role="listbox" aria-label="Places">
                {options.map((p, i) => (
                  <li
                    key={p.label}
                    id={optionId(i)}
                    role="option"
                    aria-selected={i === at}
                    onMouseDown={(e) => e.preventDefault()}
                    onMouseMove={() => i !== at && setActive(i)}
                    onClick={() => add(p)}
                    className={cn(
                      "flex h-9 cursor-pointer select-none items-center justify-between gap-3 rounded-control px-2.5 text-sm transition-[background-color] duration-100",
                      i === at ? "bg-primary-soft text-primary-ink" : "text-ink",
                    )}
                  >
                    <span className="truncate font-semibold">{p.label}</span>
                    <span className={cn("shrink-0 text-xs", i === at ? "text-primary-ink/70" : "text-muted")}>{KIND[p.kind]}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="px-2.5 py-2 text-sm text-muted">No place matches. Try a city, state or country.</p>
            )}
          </motion.div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
